"use strict";
// packages/shared-schemas/src/consent.ts
//
// Consent registry (versioned wording) and consent ledger (append-only
// grant / withdraw records) contracts.
//
//   companies/{c}/consent_texts/{versionId}     the wording a person agreed to
//   companies/{c}/consent_records/{id}          append-only events (rules deny update/delete)
//   companies/{c}/consent_state/{subjectKey}    latest state per purpose, derived from records
//
// A subject is keyed by sha256(`${companyId}:${emailNorm}`) so the state doc
// id never carries the address, and survives erasure of the `email` field.
// Consent wording lives in VALUTTEN, versioned; an active text is immutable
// (changing it means a new version), and at most one text per purpose is
// active at a time.
//
// Browser-safe: no Node built-ins here, because front ends deep-import
// `dist/<module>` (CRA 5 has no crypto polyfill). The sha256 helpers
// (consentTextSha256, consentSubjectKey) live in consentKeys.ts, server only.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.3.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConsentStateSchema = exports.ConsentPurposeStateSchema = exports.ConsentRecordSchema = exports.ConsentSourceRefSchema = exports.DOT_INSENSITIVE_DOMAINS = exports.ConsentTextActivationError = exports.ConsentTextDraftInputSchema = exports.ConsentTextSchema = exports.ConsentTextStatus = exports.CONSENT_TEXT_STATUSES = exports.CONSENT_TEXT_MAX_LENGTH = exports.CONSENT_TEXT_VERSION_ID_PATTERN = exports.ConsentEvent = exports.CONSENT_EVENTS = exports.ConsentEventSource = exports.CONSENT_EVENT_SOURCES = exports.CONSENT_PURPOSE_LABELS = exports.ConsentPurpose = exports.CONSENT_PURPOSES = exports.CONSENT_STATE_SUBCOLLECTION = exports.CONSENT_RECORDS_SUBCOLLECTION = exports.CONSENT_TEXTS_SUBCOLLECTION = void 0;
exports.planConsentTextActivation = planConsentTextActivation;
exports.findActiveConsentConflicts = findActiveConsentConflicts;
exports.normaliseConsentEmail = normaliseConsentEmail;
exports.canonicalMailbox = canonicalMailbox;
exports.applyConsentRecord = applyConsentRecord;
exports.hasConsentGrant = hasConsentGrant;
const zod_1 = require("zod");
// ─── Collections ────────────────────────────────────────────────────────────
exports.CONSENT_TEXTS_SUBCOLLECTION = 'consent_texts';
exports.CONSENT_RECORDS_SUBCOLLECTION = 'consent_records';
exports.CONSENT_STATE_SUBCOLLECTION = 'consent_state';
// ─── Purposes and sources ───────────────────────────────────────────────────
exports.CONSENT_PURPOSES = ['enquiry_contact', 'marketing_email', 'referral_third_party', 'referrer_terms'];
exports.ConsentPurpose = zod_1.z.enum(exports.CONSENT_PURPOSES);
exports.CONSENT_PURPOSE_LABELS = {
    enquiry_contact: 'Contact about my enquiry',
    marketing_email: 'Marketing emails',
    referral_third_party: 'Client agreed to be referred',
    referrer_terms: 'Referrer terms',
};
exports.CONSENT_EVENT_SOURCES = [
    'website',
    'import',
    'manual',
    'ad_platform',
    'crm',
    'backfill',
    'unsubscribe_link',
    'complaint',
    'bounce',
    'retention',
];
exports.ConsentEventSource = zod_1.z.enum(exports.CONSENT_EVENT_SOURCES);
exports.CONSENT_EVENTS = ['grant', 'withdraw'];
exports.ConsentEvent = zod_1.z.enum(exports.CONSENT_EVENTS);
// ─── Consent texts (registry) ───────────────────────────────────────────────
exports.CONSENT_TEXT_VERSION_ID_PATTERN = /^ct_[a-z0-9]{10,40}$/;
exports.CONSENT_TEXT_MAX_LENGTH = 2000;
exports.CONSENT_TEXT_STATUSES = ['draft', 'active', 'retired'];
exports.ConsentTextStatus = zod_1.z.enum(exports.CONSENT_TEXT_STATUSES);
const Sha256HexSchema = zod_1.z.string().length(64).regex(/^[0-9a-f]{64}$/, 'must be a lowercase sha256 hex digest');
exports.ConsentTextSchema = zod_1.z.object({
    versionId: zod_1.z.string().regex(exports.CONSENT_TEXT_VERSION_ID_PATTERN),
    purpose: exports.ConsentPurpose,
    text: zod_1.z.string().min(1).max(exports.CONSENT_TEXT_MAX_LENGTH),
    /** sha256 of `text` exactly as stored (UTF-8), computed server-side (consentTextSha256). */
    sha256: Sha256HexSchema,
    status: exports.ConsentTextStatus,
    label: zod_1.z.string().max(80),
    createdAt: zod_1.z.unknown(),
    createdBy: zod_1.z.string(),
    activatedAt: zod_1.z.unknown().nullable(),
    activatedBy: zod_1.z.string().nullable(),
    retiredAt: zod_1.z.unknown().nullable(),
});
/** What an admin submits to create a draft. The server adds id, hash, status and audit fields. */
exports.ConsentTextDraftInputSchema = zod_1.z.object({
    purpose: exports.ConsentPurpose,
    text: zod_1.z.string().trim().min(1).max(exports.CONSENT_TEXT_MAX_LENGTH),
    label: zod_1.z.string().trim().max(80).default(''),
});
class ConsentTextActivationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ConsentTextActivationError';
    }
}
exports.ConsentTextActivationError = ConsentTextActivationError;
/**
 * Plan activating a draft: which version becomes active and which currently
 * active versions of the same purpose must be retired in the same
 * transaction, so the "at most one active per purpose" invariant holds.
 * Only a draft can be activated (an active text is immutable, a retired one
 * stays retired). Pass `hashText` (consentTextSha256 from consentKeys.ts) to
 * also refuse a draft whose stored hash no longer matches its text.
 */
function planConsentTextActivation(texts, versionId, hashText) {
    const target = texts.find((t) => t.versionId === versionId);
    if (!target)
        throw new ConsentTextActivationError('not_found', `Consent text ${versionId} was not found.`);
    if (target.status !== 'draft') {
        throw new ConsentTextActivationError('not_draft', 'Only a draft can be activated. To change active wording, create a new version.');
    }
    if (hashText && hashText(target.text) !== target.sha256) {
        throw new ConsentTextActivationError('hash_mismatch', 'This draft changed after it was saved. Save it again before activating.');
    }
    const retire = texts
        .filter((t) => t.purpose === target.purpose && t.status === 'active' && t.versionId !== versionId)
        .map((t) => t.versionId)
        .sort();
    return { activate: versionId, retire, purpose: target.purpose };
}
/** Purposes holding more than one active text. Empty when the invariant holds. */
function findActiveConsentConflicts(texts) {
    const counts = new Map();
    for (const t of texts)
        if (t.status === 'active')
            counts.set(t.purpose, (counts.get(t.purpose) ?? 0) + 1);
    return exports.CONSENT_PURPOSES.filter((p) => (counts.get(p) ?? 0) > 1);
}
// ─── Subjects ───────────────────────────────────────────────────────────────
/** The address as consent is keyed: trimmed and lower-cased. */
function normaliseConsentEmail(email) {
    return email.trim().toLowerCase();
}
/** Providers that deliver dotted variants of a local part to one mailbox. */
exports.DOT_INSENSITIVE_DOMAINS = new Set(['gmail.com', 'googlemail.com']);
/**
 * The mailbox an address delivers to, for abuse limits, duplicate detection
 * and suppression lookups only (never stored as the identity): `+tag`
 * stripped everywhere, dots stripped for Gmail, googlemail folded to gmail.
 * Input should already be normalised (normaliseConsentEmail).
 */
function canonicalMailbox(emailNorm) {
    const at = emailNorm.lastIndexOf('@');
    if (at <= 0)
        return emailNorm;
    let local = emailNorm.slice(0, at);
    let domain = emailNorm.slice(at + 1);
    const plus = local.indexOf('+');
    if (plus > 0)
        local = local.slice(0, plus);
    if (exports.DOT_INSENSITIVE_DOMAINS.has(domain)) {
        local = local.replace(/\./g, '');
        domain = 'gmail.com';
    }
    return `${local}@${domain}`;
}
// ─── Records (ledger) ───────────────────────────────────────────────────────
exports.ConsentSourceRefSchema = zod_1.z.object({ kind: zod_1.z.string().min(1).max(64), id: zod_1.z.string().min(1).max(256) });
exports.ConsentRecordSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).max(128),
    subjectKey: zod_1.z.string().length(64),
    /** Null after erasure (retention or lead erasure); the record itself is kept, de-identified. */
    email: zod_1.z.string().nullable(),
    purpose: exports.ConsentPurpose,
    event: exports.ConsentEvent,
    /** The wording agreed to. Required for a website grant; null for withdrawals and inferred grants. */
    textVersionId: zod_1.z.string().nullable(),
    textSha256: zod_1.z.string().nullable(),
    source: exports.ConsentEventSource,
    sourceRef: exports.ConsentSourceRefSchema.nullable(),
    connectionId: zod_1.z.string().nullable(),
    sourcePath: zod_1.z.string().nullable(),
    botScore: zod_1.z.number().nullable(),
    receivedAt: zod_1.z.unknown(),
    clientAt: zod_1.z.string().nullable(),
    actorUid: zod_1.z.string().nullable(),
    backfilled: zod_1.z.boolean(),
});
exports.ConsentPurposeStateSchema = zod_1.z.object({
    granted: zod_1.z.boolean(),
    lastRecordId: zod_1.z.string(),
    at: zod_1.z.unknown(),
});
exports.ConsentStateSchema = zod_1.z.object({
    subjectKey: zod_1.z.string().length(64),
    email: zod_1.z.string().nullable(),
    purposes: zod_1.z.record(exports.ConsentPurpose, exports.ConsentPurposeStateSchema),
});
/**
 * Fold one record into a subject's state. Pure: the ledger calls it inside
 * the transaction that appends the record. The latest record per purpose
 * wins; the caller appends in time order. An erased record (email null)
 * never restores an address onto the state.
 */
function applyConsentRecord(state, record) {
    if (state && state.subjectKey !== record.subjectKey) {
        throw new RangeError('applyConsentRecord: record belongs to a different subject');
    }
    const base = state ?? { subjectKey: record.subjectKey, email: record.email, purposes: {} };
    return {
        subjectKey: base.subjectKey,
        email: base.email ?? record.email,
        purposes: {
            ...base.purposes,
            [record.purpose]: { granted: record.event === 'grant', lastRecordId: record.id, at: record.receivedAt },
        },
    };
}
/** True when the state holds a current grant for `purpose`. */
function hasConsentGrant(state, purpose) {
    return state?.purposes?.[purpose]?.granted === true;
}
